import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trash2, Plus, Minus } from 'lucide-react';
import { useCartStore } from '../../store/cart';
import { useAuthStore } from '../../store/auth';
import styles from './CartPage.module.css';

const FREE_SHIPPING_THRESHOLD = 999;
const SHIPPING_CHARGE = 99;

export default function CartPage() {
  const { t } = useTranslation();
  const { items, removeItem, updateQuantity, total } = useCartStore();
  const accessToken = useAuthStore(s => s.accessToken);
  const navigate = useNavigate();

  const subtotal = total();
  const shippingCharge = subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_CHARGE;
  const orderTotal = subtotal + shippingCharge;

  const handleCheckout = () => {
    if (!accessToken) {
      navigate('/login', { state: { from: '/checkout' } });
    } else {
      navigate('/checkout');
    }
  };

  if (items.length === 0) {
    return (
      <div className={styles.empty}>
        <h2>{t('cart.empty')}</h2>
        <Link to="/catalogue" className={styles.shopBtn}>{t('cart.continue_shopping')}</Link>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{t('cart.title')}</h1>

      <div className={styles.layout}>
        <div className={styles.items}>
          {items.map(item => (
            <div key={item.productId} className={styles.item}>
              <div className={styles.itemImage}>
                {item.imageUrl
                  ? <img src={item.imageUrl} alt={item.name} />
                  : <div className={styles.imagePlaceholder} />
                }
              </div>
              <div className={styles.itemInfo}>
                <Link to={`/products/${item.productId}`} className={styles.itemName}>{item.name}</Link>
                <p className={styles.itemPrice}>₹{item.price.toLocaleString('en-IN')}</p>
              </div>
              <div className={styles.qty}>
                <button onClick={() => updateQuantity(item.productId, item.quantity - 1)}><Minus size={14} /></button>
                <span>{item.quantity}</span>
                <button onClick={() => updateQuantity(item.productId, Math.min(item.quantity + 1, item.stockCount))}><Plus size={14} /></button>
              </div>
              <p className={styles.lineTotal}>₹{(item.price * item.quantity).toLocaleString('en-IN')}</p>
              <button className={styles.removeBtn} onClick={() => removeItem(item.productId)}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>

        <div className={styles.summary}>
          <h2 className={styles.summaryTitle}>Order Summary</h2>
          <div className={styles.row}><span>{t('cart.subtotal')}</span><span>₹{subtotal.toLocaleString('en-IN')}</span></div>
          <div className={styles.row}>
            <span>{t('cart.shipping')}</span>
            <span>{shippingCharge === 0 ? <b className={styles.free}>{t('cart.free_shipping')}</b> : `₹${shippingCharge}`}</span>
          </div>
          {shippingCharge > 0 && (
            <p className={styles.shippingNotice}>
              {t('cart.free_shipping_notice', { amount: (FREE_SHIPPING_THRESHOLD - subtotal).toLocaleString('en-IN') })}
            </p>
          )}
          <div className={`${styles.row} ${styles.totalRow}`}>
            <span>{t('cart.total')}</span>
            <span>₹{orderTotal.toLocaleString('en-IN')}</span>
          </div>
          <button className={styles.checkoutBtn} onClick={handleCheckout}>
            {t('cart.checkout')}
          </button>
          <Link to="/catalogue" className={styles.continueShopping}>{t('cart.continue_shopping')}</Link>
        </div>
      </div>
    </div>
  );
}
